import { RunPaceZone, SwimZone, BikePowerZone, HRZone, AllZones } from '../../types/athlete';

const formatPace = (secondsPerKm: number): string => {
  const mins = Math.floor(secondsPerKm / 60);
  const secs = Math.round(secondsPerKm % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const parseTimeToSeconds = (timeStr: string): number => {
  const parts = timeStr.split(':').map(Number);
  if (parts.length === 3) {
    return parts[0] * 3600 + parts[1] * 60 + parts[2];
  } else if (parts.length === 2) {
    return parts[0] * 60 + parts[1];
  }
  return 0;
};

export const calculateRunPaces = (
  referenceDistanceKm: number,
  referenceTimeStr: string
): RunPaceZone[] => {
  const refSeconds = parseTimeToSeconds(referenceTimeStr);
  const refPaceSecondsPerKm = refSeconds / referenceDistanceKm;

  const zones: RunPaceZone[] = [
    {
      name: 'Easy / Recovery',
      perKm: formatPace(refPaceSecondsPerKm * 1.25),
      perMile: formatPace(refPaceSecondsPerKm * 1.25 * 1.60934),
      description: 'Comfortable, conversational pace. Most training should be done here.',
    },
    {
      name: 'Long Run',
      perKm: formatPace(refPaceSecondsPerKm * 1.15),
      perMile: formatPace(refPaceSecondsPerKm * 1.15 * 1.60934),
      description: 'Sustained effort for building endurance. Should feel controlled.',
    },
    {
      name: 'Marathon',
      perKm: formatPace(refPaceSecondsPerKm * 1.05),
      perMile: formatPace(refPaceSecondsPerKm * 1.05 * 1.60934),
      description: 'Race pace for marathon distance. Challenging but sustainable.',
    },
    {
      name: 'Threshold (T)',
      perKm: formatPace(refPaceSecondsPerKm),
      perMile: formatPace(refPaceSecondsPerKm * 1.60934),
      description: 'Functional Threshold Pace. Comfortably hard. Can sustain ~60 min.',
    },
    {
      name: 'Interval (I)',
      perKm: formatPace(refPaceSecondsPerKm * 0.95),
      perMile: formatPace(refPaceSecondsPerKm * 0.95 * 1.60934),
      description: 'VO2max pace. Hard effort. Reps of 3-5 min.',
    },
    {
      name: 'Repetition (R)',
      perKm: formatPace(refPaceSecondsPerKm * 0.88),
      perMile: formatPace(refPaceSecondsPerKm * 0.88 * 1.60934),
      description: 'Very fast, short reps with full recovery.',
    },
  ];

  return zones;
};

export const calculateSwimZones = (cssSecondsPer100m: number): SwimZone[] => {
  const zones: SwimZone[] = [
    {
      zone: 1,
      name: 'Warm-up / Recovery',
      pacePer100m: formatPace(cssSecondsPer100m * 1.15),
      description: 'Very easy, focus on technique',
    },
    {
      zone: 2,
      name: 'Endurance',
      pacePer100m: formatPace(cssSecondsPer100m * 1.05),
      description: 'Steady aerobic swimming',
    },
    {
      zone: 3,
      name: 'Tempo',
      pacePer100m: formatPace(cssSecondsPer100m),
      description: 'CSS pace. Challenging but controlled',
    },
    {
      zone: 4,
      name: 'Threshold',
      pacePer100m: formatPace(cssSecondsPer100m * 0.95),
      description: 'Near threshold effort',
    },
    {
      zone: 5,
      name: 'VO2max',
      pacePer100m: formatPace(cssSecondsPer100m * 0.88),
      description: 'Maximum effort, short bursts',
    },
  ];

  return zones;
};

export const calculateBikePowerZones = (ftp: number): BikePowerZone[] => {
  const zones: BikePowerZone[] = [
    {
      zone: 1,
      name: 'Active Recovery',
      watts: `< ${Math.round(ftp * 0.55)}W`,
      description: 'Very easy spinning, recovery rides',
    },
    {
      zone: 2,
      name: 'Endurance',
      watts: `${Math.round(ftp * 0.56)} - ${Math.round(ftp * 0.75)}W`,
      description: 'All-day sustainable effort',
    },
    {
      zone: 3,
      name: 'Tempo',
      watts: `${Math.round(ftp * 0.76)} - ${Math.round(ftp * 0.9)}W`,
      description: 'Harder but sustainable',
    },
    {
      zone: 4,
      name: 'Threshold',
      watts: `${Math.round(ftp * 0.91)} - ${Math.round(ftp * 1.05)}W`,
      description: 'Functional Threshold. ~60 min max',
    },
    {
      zone: 5,
      name: 'VO2max',
      watts: `${Math.round(ftp * 1.06)} - ${Math.round(ftp * 1.2)}W`,
      description: 'High intensity, short efforts',
    },
    {
      zone: 6,
      name: 'Anaerobic',
      watts: `${Math.round(ftp * 1.21)} - ${Math.round(ftp * 1.5)}W`,
      description: 'Very hard, short duration',
    },
    {
      zone: 7,
      name: 'Neuromuscular',
      watts: `> ${Math.round(ftp * 1.5)}W`,
      description: 'All-out sprint power',
    },
  ];

  return zones;
};

export const calculateHRZones = (
  maxHr: number,
  method: 'max_hr' | 'karvonen' | 'joe_friel',
  restingHr?: number
): HRZone[] => {
  let zones: HRZone[] = [];

  if (method === 'max_hr') {
    zones = [
      { zone: 1, name: 'Warm-up', range: `${Math.round(maxHr * 0.5)} - ${Math.round(maxHr * 0.6)}`, description: 'Very light effort' },
      { zone: 2, name: 'Easy', range: `${Math.round(maxHr * 0.6)} - ${Math.round(maxHr * 0.7)}`, description: 'Conversational pace' },
      { zone: 3, name: 'Aerobic', range: `${Math.round(maxHr * 0.7)} - ${Math.round(maxHr * 0.8)}`, description: 'Moderate effort' },
      { zone: 4, name: 'Threshold', range: `${Math.round(maxHr * 0.8)} - ${Math.round(maxHr * 0.9)}`, description: 'Hard effort' },
      { zone: 5, name: 'VO2max', range: `${Math.round(maxHr * 0.9)} - ${Math.round(maxHr * 1.0)}`, description: 'Maximum effort' },
    ];
  } else if (method === 'karvonen' && restingHr) {
    const hrReserve = maxHr - restingHr;
    zones = [
      { zone: 1, name: 'Warm-up', range: `${restingHr + Math.round(hrReserve * 0.5)} - ${restingHr + Math.round(hrReserve * 0.6)}`, description: 'Very light effort' },
      { zone: 2, name: 'Easy', range: `${restingHr + Math.round(hrReserve * 0.6)} - ${restingHr + Math.round(hrReserve * 0.7)}`, description: 'Conversational pace' },
      { zone: 3, name: 'Aerobic', range: `${restingHr + Math.round(hrReserve * 0.7)} - ${restingHr + Math.round(hrReserve * 0.8)}`, description: 'Moderate effort' },
      { zone: 4, name: 'Threshold', range: `${restingHr + Math.round(hrReserve * 0.8)} - ${restingHr + Math.round(hrReserve * 0.9)}`, description: 'Hard effort' },
      { zone: 5, name: 'VO2max', range: `${restingHr + Math.round(hrReserve * 0.9)} - ${restingHr + Math.round(hrReserve * 1.0)}`, description: 'Maximum effort' },
    ];
  } else if (method === 'joe_friel') {
    zones = [
      { zone: 1, name: 'Active Recovery', range: `${Math.round(maxHr * 0.55)} - ${Math.round(maxHr * 0.69)}`, description: 'Very easy, recovery' },
      { zone: 2, name: 'Endurance', range: `${Math.round(maxHr * 0.7)} - ${Math.round(maxHr * 0.79)}`, description: 'Long slow distance' },
      { zone: 3, name: 'Tempo', range: `${Math.round(maxHr * 0.8)} - ${Math.round(maxHr * 0.89)}`, description: 'Comfortably hard' },
      { zone: 4, name: 'Threshold', range: `${Math.round(maxHr * 0.9)} - ${Math.round(maxHr * 0.94)}`, description: 'Just below lactate threshold' },
      { zone: 5, name: 'VO2max', range: `${Math.round(maxHr * 0.95)} - ${Math.round(maxHr * 1.0)}`, description: 'Maximum aerobic capacity' },
    ];
  }

  return zones;
};

export const calculateAllZones = (
  settings: {
    css_value: number | null;
    cycling_ftp: number | null;
    run_reference_distance: number | null;
    run_reference_time: string | null;
    max_hr: number | null;
    resting_hr: number | null;
    hr_zone_method: 'max_hr' | 'karvonen' | 'joe_friel' | null;
  }
): AllZones => {
  const allZones: AllZones = {
    run: [],
    swim: [],
    bike: [],
    hr: [],
  };

  if (settings.run_reference_distance && settings.run_reference_time) {
    allZones.run = calculateRunPaces(settings.run_reference_distance, settings.run_reference_time);
  }

  if (settings.css_value) {
    allZones.swim = calculateSwimZones(settings.css_value);
  }

  if (settings.cycling_ftp) {
    allZones.bike = calculateBikePowerZones(settings.cycling_ftp);
  }

  if (settings.max_hr && settings.hr_zone_method) {
    allZones.hr = calculateHRZones(
      settings.max_hr,
      settings.hr_zone_method,
      settings.hr_zone_method === 'karvonen' ? settings.resting_hr || undefined : undefined
    );
  }

  return allZones;
};
